import {
  ACTIVE_OPT_JOB_STATUSES,
  OPT_JOB_TYPES_VISIBLE_IN_LOG,
  OptJobWithQueueItems,
  TERMINAL_OPT_JOB_STATUSES,
  useOptJobsWithQueueItems,
} from 'api/optJobs/optJobs.api'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import Datetime from 'utils/datetime/datetime'
import styles from '../NotificationCenter.module.less'
import { matchesOptimizationFilters } from './NewNotificationOptimizationLogs.helper'
import { OptJobLogRow } from './OptJobLogRow'

type NewNotificationOptimizationLogsProps = {
  selectedFilters: string[]
  date: ISODateTime
  treatRegularAsPublished: boolean
}

type OptJobRowsProps = {
  jobs: OptJobWithQueueItems[]
  emptyLabel: string
  treatRegularAsPublished: boolean
}

function OptJobRows({ jobs, emptyLabel, treatRegularAsPublished }: OptJobRowsProps): ReactNode {
  if (jobs.length === 0) return <div className={styles.NotificationCenter_Events__noEvents}>{emptyLabel}</div>

  return jobs.map((job) => (
    <OptJobLogRow key={job.id} treatRegularAsPublished={treatRegularAsPublished} optJobWithQueueItems={job} />
  ))
}

/**
 * The optimization log built from opt jobs and their queue items, behind the
 * `enable_notification_center_with_new_queueitem_statuses` system flag. Its
 * predecessor, {@link NotificationOptimizationLogs}, reads backend notifications
 * instead and goes away with the flag.
 */
export function NewNotificationOptimizationLogs({
  selectedFilters,
  date,
  treatRegularAsPublished,
}: NewNotificationOptimizationLogsProps): ReactNode {
  const { t } = useTranslation()

  const { data: optJobs } = useOptJobsWithQueueItems({
    createdAfter: Datetime.getStartOfDay(date),
    createdBefore: Datetime.getEndOfDay(date),
    optJobTypes: OPT_JOB_TYPES_VISIBLE_IN_LOG,
    optJobStatuses: ['Created', 'Queued', 'Running', 'Finished', 'Failed', 'Unsolvable'],
  })

  const matchingJobs = (optJobs ?? []).filter((job) => matchesOptimizationFilters(job, selectedFilters))
  const runningJobs = matchingJobs.filter((job) => ACTIVE_OPT_JOB_STATUSES.has(job.status))
  const completedJobs = matchingJobs.filter((job) => TERMINAL_OPT_JOB_STATUSES.has(job.status))

  return (
    <>
      <div className={styles.NotificationCenter_Events}>
        <span>{t('Running')}</span>
        <OptJobRows
          jobs={runningJobs}
          emptyLabel={t('No running optimizations')}
          treatRegularAsPublished={treatRegularAsPublished}
        />
      </div>

      <div className={styles.NotificationCenter_Events}>
        <span>{t('Completed')}</span>
        <div className={styles.NotificationCenter_Events_Container}>
          <OptJobRows
            jobs={completedJobs}
            emptyLabel={t('No completed optimizations')}
            treatRegularAsPublished={treatRegularAsPublished}
          />
        </div>
      </div>
    </>
  )
}
